import {
  Box,
  BoxProps,
  Breadcrumb,
  Flex,
  HStack,
  Heading,
  Text,
} from "@chakra-ui/react";
import { Fragment } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { getBreadcrumbForPath, type BreadcrumbItem } from "@/utils/routes";
import { MOBILE_TOPBAR_HEIGHT } from "../navbars/MobileTopBar";

export interface PageContainerProps extends BoxProps {
  title?: string;
  description?: string;
  actions?: React.ReactNode;
  headerNote?: React.ReactNode;
  breadcrumb?: BreadcrumbItem[];
  contentMaxW?: BoxProps["maxW"];
}

export const PageContainer = ({
  children,
  title,
  description,
  actions,
  headerNote,
  breadcrumb,
  contentMaxW,
  ...props
}: PageContainerProps) => {
  const location = useLocation();
  const items = breadcrumb ?? getBreadcrumbForPath(location.pathname) ?? [];

  return (
    <Box as="main" minH="100dvh" display="flex" flexDirection="column">
      <Box
        as="header"
        bg="bg.panel"
        borderBottomWidth="1px"
        borderColor="border"
        px={{ base: 4, md: 8 }}
        py={{ base: 3, md: 4 }}
        position="sticky"
        top={{ base: MOBILE_TOPBAR_HEIGHT, lg: 0 }}
        zIndex={10}
      >
        <Flex
          align="center"
          justify="space-between"
          gap={4}
          wrap="wrap"
          maxW={contentMaxW}
          mx="auto"
        >
          <Box minW={0}>
            {items.length > 0 && (
              <Breadcrumb.Root size="sm" mb={1} color="fg.muted">
                <Breadcrumb.List>
                  {items.map((item, index) => {
                    const isLast = index === items.length - 1;
                    return (
                      <Fragment key={`${item.label}-${index}`}>
                        {index > 0 && <Breadcrumb.Separator />}
                        <Breadcrumb.Item>
                          {isLast ? (
                            <Breadcrumb.CurrentLink>
                              {item.label}
                            </Breadcrumb.CurrentLink>
                          ) : item.href ? (
                            <Breadcrumb.Link asChild>
                              <RouterLink to={item.href}>
                                {item.label}
                              </RouterLink>
                            </Breadcrumb.Link>
                          ) : (
                            <Breadcrumb.Link as="span" cursor="default">
                              {item.label}
                            </Breadcrumb.Link>
                          )}
                        </Breadcrumb.Item>
                      </Fragment>
                    );
                  })}
                </Breadcrumb.List>
              </Breadcrumb.Root>
            )}
            {title && (
              <Heading
                as="h2"
                size={{ base: "xl", md: "2xl" }}
                fontWeight="semibold"
                letterSpacing="tight"
                color="fg"
              >
                {title}
              </Heading>
            )}
            {description && (
              <Text mt={1} fontSize="sm" color="fg.muted">
                {description}
              </Text>
            )}
          </Box>
          {(actions || headerNote) && (
            <HStack gap={3} flexWrap="wrap" w={{ base: "full", md: "auto" }}>
              {headerNote && (
                <Text fontSize="sm" color="fg.muted">
                  {headerNote}
                </Text>
              )}
              {actions}
            </HStack>
          )}
        </Flex>
      </Box>

      <Box
        flex={1}
        px={{ base: 3, md: 8 }}
        pt={{ base: 4, md: 6 }}
        pb={{ base: 4, md: 6 }}
        w="full"
        maxW={contentMaxW}
        mx="auto"
        {...props}
      >
        {children}
      </Box>
    </Box>
  );
};
